import React from 'react';
import { methodSteps } from '../data/mock';
import { CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimateOnScroll } from '../components/AnimateOnScroll';

const BOOKING_URL = "https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled";

const stepGradients = [
  'from-blue-500 to-cyan-500',
  'from-orange-500 to-pink-500',
  'from-purple-500 to-indigo-500',
  'from-cyan-500 to-blue-500'
];

export const Methode = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[800px] right-0 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      {/* Hero */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50 relative">
        <div className="mx-auto max-w-4xl text-center relative z-10">
          <AnimateOnScroll>
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Processus en 4 étapes
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ma méthode de travail
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
              Un processus clair en 4 étapes pour vous garantir un accompagnement structuré, 
              transparent et adapté à votre entreprise.
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-5xl">
          <div className="space-y-8 sm:space-y-12">
            {methodSteps.map((step, index) => (
              <AnimateOnScroll key={step.number} delay={index * 100}>
                <div className="relative">
                  {/* Connector line */}
                  {index < methodSteps.length - 1 && (
                    <div className="hidden md:block absolute left-8 top-20 w-0.5 h-full bg-gradient-to-b from-blue-300 to-transparent -z-10" />
                  )}
                  
                  <div className="flex flex-col md:flex-row gap-5 sm:gap-6 items-start">
                    {/* Number circle */}
                    <div className="flex-shrink-0">
                      <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${stepGradients[index]} text-white flex items-center justify-center text-xl sm:text-2xl font-bold shadow-xl`}>
                        {step.number}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow duration-300">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                        {step.title}
                      </h2>
                      <p className="text-blue-600 font-medium mb-4 text-sm sm:text-base">{step.duration}</p>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Principes */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 relative z-10">
        <div className="mx-auto max-w-4xl">
          <AnimateOnScroll>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
              Mes principes
            </h2>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {[
              {
                title: 'Transparence',
                description: 'Vous savez toujours ce qui est fait, pourquoi et combien cela coûte.'
              },
              {
                title: 'Pédagogie',
                description: 'Je vulgarise et explique pour que vous compreniez les enjeux et les solutions.'
              },
              {
                title: 'Pragmatisme',
                description: 'Des solutions adaptées à votre contexte réel, pas de sur-ingénierie.'
              },
              {
                title: 'Continuité',
                description: 'Je reste votre interlocuteur dans la durée pour un suivi cohérent.'
              }
            ].map((principe, index) => (
              <AnimateOnScroll key={index} delay={index * 80}>
                <div className="flex items-start space-x-3 bg-white p-5 sm:p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{principe.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600">{principe.description}</p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <AnimateOnScroll>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Prêt à démarrer ?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8">
              La première étape est un simple échange de 30 minutes pour comprendre votre situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Phone className="w-5 h-5 mr-2" />
                  Prendre rendez-vous (30 min)
                </a>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </div>
  );
};
